import React from 'react';
import { AlertTriangle, Plus, Trash2, X } from 'lucide-react';

const VARIANTS = [['did_you_know', 'Did you know?'], ['regulatory', 'Regulatory note'], ['pitfall', 'Common pitfall'], ['case', 'Real-world case']];

export const countFlags = (blocks) => (blocks || []).reduce((n, b) => n + Object.keys(b.flags || {}).length, 0);

// Text field that highlights NAVI fact-check flags; editing it clears the flag.
const Field = ({ block, path, value, onEdit, multiline, placeholder, className = '', testId }) => {
  const reason = block.flags?.[path];
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div className={className}>
      <Tag value={value || ''} placeholder={placeholder} rows={multiline ? 3 : undefined} onChange={(e) => onEdit(path, e.target.value)}
        className={`w-full rounded-md bg-zinc-900 border px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 resize-y ${
          reason ? 'border-amber-400/70 bg-amber-400/10 focus:ring-amber-300' : 'border-zinc-700 focus:ring-teal-400'}`}
        title={reason ? `Not found in section text: please verify. ${reason}` : undefined} data-testid={testId} data-flagged={reason ? 'true' : undefined} />
      {reason && <p className="mt-1 text-[11px] text-amber-300 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />Not found in section text: please verify. {reason}</p>}
    </div>
  );
};

const setPath = (block, path, value) => {
  const b = JSON.parse(JSON.stringify(block));
  const keys = path.split('.');
  let o = b;
  keys.slice(0, -1).forEach((k) => { o = o[k]; });
  o[keys[keys.length - 1]] = value;
  if (b.flags) { delete b.flags[path]; if (!Object.keys(b.flags).length) delete b.flags; }
  return b;
};

// Removing a list item shifts indexes, so flags on later items are dropped (re-check to refresh them).
const withoutFlags = (b, prefix) => {
  if (!b.flags) return b;
  const flags = Object.fromEntries(Object.entries(b.flags).filter(([k]) => !k.startsWith(prefix)));
  return Object.keys(flags).length ? { ...b, flags } : (({ flags: _f, ...rest }) => rest)(b);
};

const SmallBtn = ({ onClick, children, testId, danger }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${danger ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}>{children}</button>
);

const TableEditor = ({ b, edit, update, t }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[560px] table-fixed border-separate border-spacing-1">
      <thead>
        <tr>{b.headers.map((h, j) => <th key={j} className="font-normal"><Field block={b} path={`headers.${j}`} value={h} onEdit={edit} testId={`${t}-header-${j}`} /></th>)}<th className="w-8" /></tr>
      </thead>
      <tbody>
        {b.rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => <td key={j} className="align-top"><Field block={b} path={`rows.${i}.${j}`} value={c} onEdit={edit} multiline testId={`${t}-cell-${i}-${j}`} /></td>)}
            <td className="align-top"><button type="button" onClick={() => update(withoutFlags({ ...b, rows: b.rows.filter((_, k) => k !== i) }, 'rows.'))} className="p-1.5 text-zinc-500 hover:text-rose-300" data-testid={`${t}-remove-row-${i}`}><X className="h-4 w-4" /></button></td>
          </tr>
        ))}
      </tbody>
    </table>
    <SmallBtn onClick={() => update({ ...b, rows: [...b.rows, b.headers.map(() => '')] })} testId={`${t}-add-row`}><Plus className="h-3 w-3" />Add row</SmallBtn>
  </div>
);

const ListEditor = ({ b, edit, update, t, listKey, fields }) => (
  <div className="space-y-2">
    {b[listKey].map((item, i) => (
      <div key={i} className="flex gap-2 items-start rounded-lg border border-zinc-800 p-2">
        <span className="h-6 w-6 shrink-0 rounded-full bg-zinc-800 text-zinc-400 text-xs flex items-center justify-center mt-1">{i + 1}</span>
        <div className="flex-1 grid gap-2 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]">
          {fields.map(([f, ph, multi]) => <Field key={f} block={b} path={`${listKey}.${i}.${f}`} value={item[f]} placeholder={ph} multiline={multi} onEdit={edit} testId={`${t}-${listKey}-${i}-${f}`} />)}
        </div>
        <button type="button" onClick={() => update(withoutFlags({ ...b, [listKey]: b[listKey].filter((_, k) => k !== i) }, `${listKey}.`))} className="p-1.5 text-zinc-500 hover:text-rose-300" data-testid={`${t}-remove-${listKey}-${i}`}><X className="h-4 w-4" /></button>
      </div>
    ))}
    <SmallBtn onClick={() => update({ ...b, [listKey]: [...b[listKey], Object.fromEntries(fields.map(([f]) => [f, '']))] })} testId={`${t}-add-${listKey}`}><Plus className="h-3 w-3" />Add {listKey === 'events' ? 'event' : 'step'}</SmallBtn>
  </div>
);

const BlockEditor = ({ b, edit, update, t }) => {
  if (b.type === 'table') return <TableEditor b={b} edit={edit} update={update} t={t} />;
  if (b.type === 'timeline') return <ListEditor b={b} edit={edit} update={update} t={t} listKey="events" fields={[['date', 'Date / period'], ['title', 'Event'], ['description', 'Description', true]]} />;
  if (b.type === 'process') return <ListEditor b={b} edit={edit} update={update} t={t} listKey="steps" fields={[['title', 'Step'], ['description', 'Description', true]]} />;
  return (
    <div className="space-y-2">
      <select value={b.variant} onChange={(e) => update({ ...b, variant: e.target.value })} className="h-8 rounded-md bg-zinc-900 border border-zinc-700 text-sm text-white px-2" data-testid={`${t}-variant`}>
        {VARIANTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <Field block={b} path="text" value={b.text} onEdit={edit} multiline testId={`${t}-text`} />
    </div>
  );
};

const LABEL = { table: 'Table', timeline: 'Timeline', process: 'Process flow', callout: 'Callout' };

// Inline editor for NAVI visual blocks (used in NAVI's preview and for saved visuals).
export default function EditableVisualBlocks({ blocks, onChange, testIdPrefix = 'visual-edit' }) {
  if (!blocks?.length) return <p className="text-sm text-zinc-500 my-4">No visuals left. Save to remove visuals from this section.</p>;
  const update = (i, nb) => onChange(blocks.map((b, k) => (k === i ? nb : b)));
  const flags = countFlags(blocks);
  return (
    <div className="space-y-5 my-4" data-testid={`${testIdPrefix}-list`}>
      {flags > 0 && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-200 flex items-center gap-2" data-testid={`${testIdPrefix}-flag-summary`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />{flags} item{flags === 1 ? '' : 's'} NAVI couldn't find in the section text. Check or edit the highlighted fields.
        </div>
      )}
      {blocks.map((b, i) => {
        const t = `${testIdPrefix}-${i}`;
        return (
          <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-3" data-testid={t}>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-navigate-gold font-semibold shrink-0">{LABEL[b.type]}</span>
              <Field block={b} path="title" value={b.title} placeholder="Title" onEdit={(p, v) => update(i, setPath(b, p, v))} className="flex-1" testId={`${t}-title`} />
              <SmallBtn danger onClick={() => onChange(blocks.filter((_, k) => k !== i))} testId={`${t}-delete`}><Trash2 className="h-3 w-3" />Delete</SmallBtn>
            </div>
            <BlockEditor b={b} t={t} edit={(p, v) => update(i, setPath(b, p, v))} update={(nb) => update(i, nb)} />
          </div>
        );
      })}
    </div>
  );
}
